import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const PROPERTY_TYPES = [
  'House', 'Apartment', 'Townhouse', 'Land', 'Commercial',
  'Bachelor Rooms', 'Farm', 'Vacant Land', 'Industrial'
];

const AMENITIES = [
  { id: 'pool', label: 'Swimming Pool', icon: 'fa-swimming-pool' },
  { id: 'garden', label: 'Garden', icon: 'fa-tree' },
  { id: 'garage', label: 'Garage', icon: 'fa-warehouse' },
  { id: 'fiber', label: 'Fiber Internet', icon: 'fa-wifi' },
  { id: 'pet-friendly', label: 'Pet Friendly', icon: 'fa-paw' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'beds_desc', label: 'Most Bedrooms' }
];

const AdvancedFilters = ({ 
  filters, 
  onFiltersChange, 
  onSearch, 
  onClearFilters,
  activeFiltersCount = 0,
  isMobile = false 
}) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const suggestionRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchAbortRef = useRef(null);
  // Track whether the location input had focus, so we can restore it after async state updates
  const inputHadFocusRef = useRef(false);

  // Local state for inputs that shouldn't trigger immediate re-renders
  const [localLocationQuery, setLocalLocationQuery] = useState('');
  const [localPriceRange, setLocalPriceRange] = useState([0, 50000000]);
  const [localMinPrice, setLocalMinPrice] = useState('');
  const [localMaxPrice, setLocalMaxPrice] = useState('');
  const [localMinSize, setLocalMinSize] = useState('');
  const [localMaxSize, setLocalMaxSize] = useState('');

  // Local copy of filters for the form
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync local filters when panel opens or external filters change
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setLocalPriceRange([filters.minPrice || 0, filters.maxPrice || 50000000]);
      setLocalMinPrice(filters.minPrice ? String(filters.minPrice) : '');
      setLocalMaxPrice(filters.maxPrice && filters.maxPrice < 50000000 ? String(filters.maxPrice) : '');
      setLocalMinSize(filters.minSize ? String(filters.minSize) : '');
      setLocalMaxSize(filters.maxSize ? String(filters.maxSize) : '');
    }
  }, [isOpen, filters]);

  // Fetch provinces on mount
  useEffect(() => {
    fetchProvinces();
  }, []);

  // Fetch cities when province changes
  useEffect(() => {
    if (localFilters.province) {
      fetchCities(localFilters.province);
    } else {
      setCities([]);
    }
  }, [localFilters.province]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(`${API}/locations/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const fetchCities = async (province) => {
    try {
      const response = await axios.get(`${API}/locations/cities/${province}`);
      setCities(response.data.cities);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // Debounced location search - doesn't affect parent state
  const handleLocationInputChange = useCallback((value) => {
    setLocalLocationQuery(value);
    // Clear inline error as the user edits the query
    setSearchError('');

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Abort any in-flight request from a previous keystroke
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }

    if (value.length < 2) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
      return;
    }

    // Debounce the API call (500ms as per spec)
    searchTimeoutRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      // Fail-safe: cap any request at 8s so we always resolve to either results or an inline error
      const hardTimeout = setTimeout(() => controller.abort(), 8000);

      setIsSearching(true);
      try {
        const response = await axios.get(
          `${API}/locations/search?q=${encodeURIComponent(value)}`,
          { timeout: 7000, signal: controller.signal }
        );
        setLocationSuggestions(response.data);
        setShowSuggestions(true);
        setSearchError('');
      } catch (error) {
        // Ignore aborts (user typed another letter) — keep the modal calm
        if (axios.isCancel?.(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
          return;
        }
        const timedOut = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
        setLocationSuggestions([]);
        setShowSuggestions(false);
        setSearchError(timedOut ? 'Search timed out. Please try again.' : 'Could not load suggestions.');
      } finally {
        clearTimeout(hardTimeout);
        if (searchAbortRef.current === controller) {
          searchAbortRef.current = null;
        }
        setIsSearching(false);
      }
    }, 500);
  }, []);

  const handleLocationSelect = (location) => {
    if (location.type === 'province') {
      setLocalFilters(prev => ({
        ...prev,
        province: location.slug,
        city: '',
        suburb: ''
      }));
      setLocalLocationQuery(location.name);
    } else {
      setLocalFilters(prev => ({
        ...prev,
        province: location.provinceSlug,
        city: location.name,
        suburb: ''
      }));
      setLocalLocationQuery(location.displayName);
    }
    setShowSuggestions(false);
  };

  const handlePropertyTypeToggle = (type) => {
    const currentTypes = localFilters.propertyTypes || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];
    setLocalFilters(prev => ({ ...prev, propertyTypes: newTypes }));
  };

  const handleAmenityToggle = (amenityId) => {
    const currentAmenities = localFilters.amenities || [];
    const newAmenities = currentAmenities.includes(amenityId)
      ? currentAmenities.filter(a => a !== amenityId)
      : [...currentAmenities, amenityId];
    setLocalFilters(prev => ({ ...prev, amenities: newAmenities }));
  };

  const formatPrice = (value) => {
    if (value >= 1000000) {
      return `R${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `R${(value / 1000).toFixed(0)}K`;
    }
    return `R${value}`;
  };

  // Handle Apply Filters - this is the only time we update parent state
  const handleApplyFilters = () => {
    const finalFilters = {
      ...localFilters,
      minPrice: localPriceRange[0] || parseInt(localMinPrice) || null,
      maxPrice: localPriceRange[1] < 50000000 ? localPriceRange[1] : (parseInt(localMaxPrice) || null),
      minSize: parseInt(localMinSize) || null,
      maxSize: parseInt(localMaxSize) || null
    };
    
    onFiltersChange(finalFilters);
    onSearch();
    setIsOpen(false);
  };

  // Handle Clear Filters
  const handleClearFilters = () => {
    const clearedFilters = {
      province: '',
      city: '',
      suburb: '',
      listingType: '',
      propertyTypes: [],
      minPrice: null,
      maxPrice: null,
      minBeds: null,
      minBaths: null,
      minSize: null,
      maxSize: null,
      amenities: [],
      sortBy: 'newest'
    };
    
    setLocalFilters(clearedFilters);
    setLocalLocationQuery('');
    setLocalPriceRange([0, 50000000]);
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalMinSize('');
    setLocalMaxSize('');
    
    if (onClearFilters) {
      onClearFilters();
    }
  };

  // Handle price slider change - only update local state
  const handlePriceSliderChange = useCallback((values) => {
    setLocalPriceRange(values);
    setLocalMinPrice(values[0] > 0 ? String(values[0]) : '');
    setLocalMaxPrice(values[1] < 50000000 ? String(values[1]) : '');
  }, []);

  // Handle price input changes
  const handleMinPriceChange = (value) => {
    setLocalMinPrice(value);
    const numValue = parseInt(value) || 0;
    setLocalPriceRange(prev => [numValue, prev[1]]);
  };

  const handleMaxPriceChange = (value) => {
    setLocalMaxPrice(value);
    const numValue = parseInt(value) || 50000000;
    setLocalPriceRange(prev => [prev[0], numValue]);
  };

  // NOTE: We intentionally build the JSX as a variable (NOT a nested component function).
  // Declaring a nested component inside the render causes React to create a new component
  // type on every render, which unmounts/remounts the subtree — destroying the <Input>
  // DOM node and dismissing the mobile keyboard. Using a plain JSX value keeps the same
  // DOM nodes across re-renders so focus is preserved.
  const filterContent = (
    <div className="space-y-6">
      {/* Location Search with Auto-suggest */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Location</Label>
        <div className="relative">
          <Input
            ref={inputRef}
            placeholder="Search province, city, or suburb..."
            value={localLocationQuery}
            onChange={(e) => handleLocationInputChange(e.target.value)}
            onFocus={() => {
              inputHadFocusRef.current = true;
              if (locationSuggestions.length > 0) setShowSuggestions(true);
            }}
            onBlur={() => { inputHadFocusRef.current = false; }}
            className="pr-10"
            data-testid="location-search-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {isSearching ? (
            <i className="fas fa-spinner fa-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true"></i>
          ) : (
            <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true"></i>
          )}

          {/* Location Suggestions Dropdown */}
          {showSuggestions && locationSuggestions.length > 0 && (
            <div
              ref={suggestionRef}
              className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {locationSuggestions.map((location, index) => (
                <button
                  key={index}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleLocationSelect(location)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                >
                  <i className={`fas ${location.type === 'province' ? 'fa-map' : 'fa-map-marker-alt'} text-gray-400`}></i>
                  <span>{location.displayName}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {location.type}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline error (does not unmount the input) */}
        {searchError && (
          <p
            className="text-xs text-red-600 flex items-center gap-1"
            role="status"
            aria-live="polite"
            data-testid="location-search-error"
          >
            <i className="fas fa-exclamation-circle"></i>
            {searchError}
          </p>
        )}

        {/* Province & City Dropdowns */}
        <div className="grid grid-cols-2 gap-2">
          <Select
            value={localFilters.province || 'all'}
            onValueChange={(value) => setLocalFilters(prev => ({ ...prev, province: value === 'all' ? '' : value, city: '' }))}
          >
            <SelectTrigger data-testid="province-select">
              <SelectValue placeholder="Province" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Provinces</SelectItem>
              {provinces.map((province) => (
                <SelectItem key={province.slug} value={province.slug}>
                  {province.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={localFilters.city || 'all'}
            onValueChange={(value) => setLocalFilters(prev => ({ ...prev, city: value === 'all' ? '' : value }))}
            disabled={!localFilters.province}
          >
            <SelectTrigger data-testid="city-select">
              <SelectValue placeholder="City / Town" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Listing Type */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Listing Type</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={localFilters.listingType === 'sale' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLocalFilters(prev => ({ 
              ...prev, 
              listingType: prev.listingType === 'sale' ? '' : 'sale' 
            }))}
            className={localFilters.listingType === 'sale' ? 'bg-emerald-600' : ''}
            data-testid="filter-for-sale"
          >
            For Sale
          </Button>
          <Button
            type="button"
            variant={localFilters.listingType === 'rental' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLocalFilters(prev => ({ 
              ...prev, 
              listingType: prev.listingType === 'rental' ? '' : 'rental' 
            }))}
            className={localFilters.listingType === 'rental' ? 'bg-emerald-600' : ''}
            data-testid="filter-to-rent"
          >
            To Rent
          </Button>
        </div>
      </div>

      {/* Property Type */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Property Type</Label>
        <div className="flex flex-wrap gap-2">
          {PROPERTY_TYPES.map((type) => (
            <Badge
              key={type}
              variant={localFilters.propertyTypes?.includes(type) ? 'default' : 'outline'}
              className={`cursor-pointer ${
                localFilters.propertyTypes?.includes(type) 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => handlePropertyTypeToggle(type)}
              data-testid={`filter-type-${type.toLowerCase().replace(' ', '-')}`}
            >
              {type}
            </Badge>
          ))}
        </div>
      </div>

      {/* Price Range - Now uses local state only */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-semibold">Price Range</Label>
          <span className="text-sm text-gray-600">
            {formatPrice(localPriceRange[0])} - {formatPrice(localPriceRange[1])}
          </span>
        </div>
        <div className="px-2">
          <Slider
            min={0}
            max={50000000}
            step={100000}
            value={localPriceRange}
            onValueChange={handlePriceSliderChange}
            className="w-full"
            data-testid="price-slider"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min Price"
            value={localMinPrice}
            onChange={(e) => handleMinPriceChange(e.target.value)}
            data-testid="min-price-input"
          />
          <Input
            type="number"
            placeholder="Max Price"
            value={localMaxPrice}
            onChange={(e) => handleMaxPriceChange(e.target.value)}
            data-testid="max-price-input"
          />
        </div>
      </div>

      {/* Bedrooms & Bathrooms */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Bedrooms</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((num) => (
              <Button
                key={num}
                type="button"
                size="sm"
                variant={localFilters.minBeds === num ? 'default' : 'outline'}
                onClick={() => setLocalFilters(prev => ({ 
                  ...prev, 
                  minBeds: prev.minBeds === num ? null : num 
                }))}
                className={`flex-1 ${localFilters.minBeds === num ? 'bg-emerald-600' : ''}`}
                data-testid={`filter-beds-${num}`}
              >
                {num}+
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-semibold">Bathrooms</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((num) => (
              <Button
                key={num}
                type="button"
                size="sm"
                variant={localFilters.minBaths === num ? 'default' : 'outline'}
                onClick={() => setLocalFilters(prev => ({ 
                  ...prev, 
                  minBaths: prev.minBaths === num ? null : num 
                }))}
                className={`flex-1 ${localFilters.minBaths === num ? 'bg-emerald-600' : ''}`}
                data-testid={`filter-baths-${num}`}
              >
                {num}+
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Size Range */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Floor Size (sqm)</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min sqm"
            value={localMinSize}
            onChange={(e) => setLocalMinSize(e.target.value)}
            data-testid="min-size-input"
          />
          <Input
            type="number"
            placeholder="Max sqm"
            value={localMaxSize}
            onChange={(e) => setLocalMaxSize(e.target.value)}
            data-testid="max-size-input"
          />
        </div>
      </div>

      {/* Amenities */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Amenities</Label>
        <div className="space-y-2">
          {AMENITIES.map((amenity) => (
            <label
              key={amenity.id}
              className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50"
            >
              <Checkbox
                checked={localFilters.amenities?.includes(amenity.id) || false}
                onCheckedChange={() => handleAmenityToggle(amenity.id)}
                data-testid={`filter-amenity-${amenity.id}`}
              />
              <i className={`fas ${amenity.icon} text-gray-500 w-5`}></i>
              <span className="text-sm">{amenity.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Sort By */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Sort By</Label>
        <Select
          value={localFilters.sortBy || 'newest'}
          onValueChange={(value) => setLocalFilters(prev => ({ ...prev, sortBy: value }))}
        >
          <SelectTrigger data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 border-t sticky bottom-0 bg-white pb-2">
        <Button 
          type="button"
          variant="outline" 
          onClick={handleClearFilters}
          className="flex-1"
          data-testid="clear-filters-btn"
        >
          <i className="fas fa-times mr-2"></i> Clear All
        </Button>
        <Button 
          type="button"
          onClick={handleApplyFilters}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          data-testid="apply-filters-btn"
        >
          <i className="fas fa-search mr-2"></i> Apply Filters
        </Button>
      </div>
    </div>
  );

  // Mobile: Sheet (Bottom drawer)
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" className="relative" data-testid="advanced-filters-btn-mobile">
            <i className="fas fa-sliders-h mr-2"></i> Filters
            {activeFiltersCount > 0 && (
              <Badge className="absolute -top-2 -right-2 bg-emerald-600 h-5 w-5 flex items-center justify-center p-0">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <i className="fas fa-filter text-emerald-600"></i>
              Advanced Filters
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(85vh-100px)] pr-4">
            <div className="py-4">
              {filterContent}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Dialog/Modal
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Button 
        variant="outline" 
        onClick={() => setIsOpen(true)} 
        className="relative"
        data-testid="advanced-filters-btn"
      >
        <i className="fas fa-sliders-h mr-2"></i> Advanced Filters
        {activeFiltersCount > 0 && (
          <Badge className="absolute -top-2 -right-2 bg-emerald-600 h-5 w-5 flex items-center justify-center p-0">
            {activeFiltersCount}
          </Badge>
        )}
      </Button>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <i className="fas fa-filter text-emerald-600"></i>
            Advanced Filters
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[calc(85vh-150px)] pr-4">
          <div className="py-4">
            {filterContent}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedFilters;
